import { AloftPrice, CompType, schAloftPrice } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import z from 'zod';

import { CompPropertyDataState, RootState } from '../../types.ts';
import { selectCompAdjustments } from './adjustmentSelectors.ts';
import { selectCompSequence } from './orderSelectors.ts';
import { selectTransactionEntities } from './propertyDataSelectors/transactionSelectors.ts';
import { selectSubjectArvValue, selectSubjectValue } from './selectSubjectValue.ts';

type ReconciliationDataParams = {
  subjectValue: AloftPrice | undefined;
  compAdjustments: Record<string, any>;
  transactionEntities: Record<string, any>;
  compIds: string[];
};

export const schReconciliationSummarizedCostData = z.object({
  costApproachValue: schAloftPrice.optional(),
  maxCompMarketAdjustedSalePrice: schAloftPrice.optional(),
  minCompMarketAdjustedSalePrice: schAloftPrice.optional(),
  averageCompMarketAdjustedSalePrice: schAloftPrice.optional(),
  medianCompMarketAdjustedSalePrice: schAloftPrice.optional(),
  averageCompDaysOnMarket: z.number().int().nonnegative().optional(),
  medianCompDaysOnMarket: z.number().int().nonnegative().optional(),
});

export type ReconciliationSummarizedCostData = z.infer<typeof schReconciliationSummarizedCostData>;
const calculateReconciliationData = ({
  subjectValue,
  compAdjustments,
  transactionEntities,
  compIds,
}: ReconciliationDataParams): ReconciliationSummarizedCostData => {
  const compMarketAdjustedSalePrices = compIds
    .map((compId) => compAdjustments[compId]?.adjustedSalePrice?.adjustment)
    .filter((price) => price !== undefined)
    .sort((a, b) => a.toDollarLike() - b.toDollarLike());
  const maxCompMarketAdjustedSalePrice =
    compMarketAdjustedSalePrices[compMarketAdjustedSalePrices.length - 1];
  const minCompMarketAdjustedSalePrice = compMarketAdjustedSalePrices[0];
  let averageCompMarketAdjustedSalePrice: AloftPrice | undefined =
    compMarketAdjustedSalePrices.reduce(
      (acc, price) => acc.add(price),
      AloftPrice.fromDollarLike(0),
    );

  if (compMarketAdjustedSalePrices.length === 0) {
    averageCompMarketAdjustedSalePrice = undefined;
  } else if (averageCompMarketAdjustedSalePrice !== undefined) {
    averageCompMarketAdjustedSalePrice = averageCompMarketAdjustedSalePrice.div(
      compMarketAdjustedSalePrices.length,
    );
  }
  const medianCompMarketAdjustedSalePrice =
    compMarketAdjustedSalePrices[Math.floor(compMarketAdjustedSalePrices.length / 2)];
  const compDaysOnMarket = compIds
    .map((key) => transactionEntities[key]?.daysOnMarket)
    .filter((value) => value !== undefined)
    .filter((value) => value > 0);
  const averageCompDaysOnMarket =
    compDaysOnMarket.length > 0
      ? Math.round(compDaysOnMarket.reduce((acc, days) => acc + days, 0) / compDaysOnMarket.length)
      : undefined;
  const medianCompDaysOnMarket =
    compDaysOnMarket.length > 0
      ? compDaysOnMarket[Math.floor(compDaysOnMarket.length / 2)]
      : undefined;

  return {
    costApproachValue: subjectValue,
    maxCompMarketAdjustedSalePrice,
    minCompMarketAdjustedSalePrice,
    averageCompMarketAdjustedSalePrice,
    medianCompMarketAdjustedSalePrice,
    averageCompDaysOnMarket,
    medianCompDaysOnMarket,
  };
};

export const selectReconciliationData = createSelector(
  [selectSubjectValue, selectCompAdjustments, selectTransactionEntities],
  (subjectValue, compAdjustments, transactionEntities) => {
    const compIds = Object.keys(transactionEntities).filter((key) => key !== 'subject');
    return calculateReconciliationData({
      subjectValue,
      compAdjustments,
      transactionEntities,
      compIds,
    });
  },
);

const selectCompsByType = createSelector(
  [selectCompSequence, (state: RootState) => state.propertyData],
  (compSequence, propertyData) => {
    const aivCompIds: string[] = [];
    const arvCompIds: string[] = [];

    compSequence.forEach((compId) => {
      const compData = propertyData.entities[compId] as CompPropertyDataState;
      const compType = compData?.compData?.compType;

      if (compType === 'aiv' || !compType) {
        aivCompIds.push(compId);
      } else if (compType === 'arv') {
        arvCompIds.push(compId);
      }
    });

    return { aivCompIds, arvCompIds };
  },
);

export const selectAivReconciliationData = createSelector(
  [selectSubjectValue, selectCompAdjustments, selectTransactionEntities, selectCompsByType],
  (subjectValue, compAdjustments, transactionEntities, { aivCompIds }) =>
    calculateReconciliationData({
      subjectValue,
      compAdjustments,
      transactionEntities,
      compIds: aivCompIds,
    }),
);

export const selectArvReconciliationData = createSelector(
  [selectSubjectArvValue, selectCompAdjustments, selectTransactionEntities, selectCompsByType],
  (subjectValue, compAdjustments, transactionEntities, { arvCompIds }) =>
    calculateReconciliationData({
      subjectValue,
      compAdjustments,
      transactionEntities,
      compIds: arvCompIds,
    }),
);

export const selectCompTypeReconciliationData = (state: RootState, compType?: CompType) => {
  if (compType === 'aiv') {
    return selectAivReconciliationData(state);
  }
  if (compType === 'arv') {
    return selectArvReconciliationData(state);
  }
  return selectReconciliationData(state);
};

export const selectAllCompTypesReconciliationData = createSelector(
  [selectReconciliationData, selectAivReconciliationData, selectArvReconciliationData],
  (generalData, aivData, arvData) => ({
    general: generalData,
    aiv: aivData,
    arv: arvData,
  }),
);
